'use client';

import { ConsentStatus, ConsentStorageAdapter, EConsentStatus } from '../types';

const DEFAULT_COOKIE_NAME = 'analyticsConsent';
const DEFAULT_MAX_AGE_DAYS = 400;
const SECONDS_PER_DAY = 24 * 60 * 60;

export const normalizeConsent = (value: unknown): boolean | null => {
    if (value === null || value === undefined) return null;
    if (typeof value === 'boolean') return value;
    if (value === 'true' || value === '"true"') return true;
    if (value === 'false' || value === '"false"') return false;
    return null;
};

const readCookie = (name: string): string | null => {
    if (typeof document === 'undefined') return null;
    const entry = document.cookie.split('; ').find((row) => row.startsWith(`${name}=`));
    if (entry === undefined) return null;
    return decodeURIComponent(entry.slice(name.length + 1));
};

const buildCookie = (name: string, value: string, maxAgeSeconds: number, domain?: string): string => {
    const parts = [
        `${name}=${encodeURIComponent(value)}`,
        'path=/',
        `max-age=${maxAgeSeconds}`,
        'SameSite=Lax',
    ];
    if (domain) {
        parts.push(`domain=${domain}`);
    }
    if (typeof window !== 'undefined' && window.location.protocol === 'https:') {
        parts.push('Secure');
    }
    return parts.join('; ');
};

const writeCookie = (name: string, value: string, maxAgeDays: number, domain?: string): void => {
    if (typeof document === 'undefined') return;
    document.cookie = buildCookie(name, value, maxAgeDays * SECONDS_PER_DAY, domain);
};

const removeCookie = (name: string, domain?: string): void => {
    if (typeof document === 'undefined') return;
    document.cookie = buildCookie(name, '', 0, domain);
};

const readLegacyLocalStorage = (key: string): boolean | null => {
    if (typeof window === 'undefined') return null;
    try {
        return normalizeConsent(window.localStorage.getItem(key));
    } catch {
        return null;
    }
};

export const resolveConsentCookieDomain = (hostname?: string): string | undefined => {
    if (!hostname) return undefined;
    if (hostname === 'localhost') return undefined;
    if (/^\d{1,3}(\.\d{1,3}){3}$/.test(hostname)) return undefined;
    const labels = hostname.split('.');
    if (labels.length < 2) return undefined;
    return `.${labels.slice(-2).join('.')}`;
};

interface ICookieConsentAdapterOptions {
    cookieName?: string;
    domain?: string;
    maxAgeDays?: number;
    legacyLocalStorageKey?: string;
}

export const createCookieConsentStorageAdapter = ({
    cookieName = DEFAULT_COOKIE_NAME,
    domain,
    maxAgeDays = DEFAULT_MAX_AGE_DAYS,
    legacyLocalStorageKey,
}: ICookieConsentAdapterOptions = {}): ConsentStorageAdapter => {
    const resolveConsent = (): boolean | null => {
        const fromCookie = normalizeConsent(readCookie(cookieName));
        if (fromCookie !== null) return fromCookie;

        if (legacyLocalStorageKey) {
            const fromLegacy = readLegacyLocalStorage(legacyLocalStorageKey);
            if (fromLegacy !== null) {
                writeCookie(cookieName, String(fromLegacy), maxAgeDays, domain);
                return fromLegacy;
            }
        }

        return null;
    };

    return {
        getConsentStatus: (): ConsentStatus => {
            const consent = resolveConsent();
            if (consent === null) return null;
            return consent ? EConsentStatus.GRANTED : EConsentStatus.DENIED;
        },
        hasUserConsented: (): boolean => resolveConsent() !== null,
        grantConsent: (): void => writeCookie(cookieName, 'true', maxAgeDays, domain),
        denyConsent: (): void => writeCookie(cookieName, 'false', maxAgeDays, domain),
        clearConsent: (): void => removeCookie(cookieName, domain),
    };
};
